import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Calendar, ArrowUpRight, ArrowDownLeft, TrendingUp, ExternalLink } from 'lucide-react';

export function StakingHistory() {
  const [filter, setFilter] = useState('all');

  // Mock transaction history
  const transactions = [
    {
      id: '1',
      type: 'stake',
      amount: 5000,
      pool: 'NDH 30-Day Lock',
      hash: '0x1234...5678',
      date: new Date(Date.now() - 24 * 60 * 60 * 1000),
      status: 'completed',
      gasUsed: '45000',
    },
    {
      id: '2',
      type: 'stake',
      amount: 1500,
      pool: 'NDH Flexible',
      hash: '0x2345...6789',
      date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      status: 'completed',
      gasUsed: '42000',
    },
    {
      id: '3',
      type: 'claim',
      amount: 89.45,
      pool: 'NDH Flexible',
      hash: '0x3456...789a',
      date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      status: 'completed',
      gasUsed: '21000',
    },
    {
      id: '4',
      type: 'unstake',
      amount: 1000,
      pool: 'NDH Flexible',
      hash: '0x4567...89ab',
      date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      status: 'completed',
      gasUsed: '38000',
    },
    {
      id: '5',
      type: 'compound',
      amount: 123.78,
      pool: 'NDH 30-Day Lock',
      hash: '0x5678...9abc',
      date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
      status: 'completed',
      gasUsed: '55000',
    },
  ];

  const analytics = {
    totalStaked: 6500,
    totalUnstaked: 1000,
    totalRewardsClaimed: 456.78,
    totalGasPaid: 0.12,
    firstStakeDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
    averageStakeDuration: 28,
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'stake':
        return <ArrowUpRight className="h-4 w-4 text-green-600" />;
      case 'unstake':
        return <ArrowDownLeft className="h-4 w-4 text-red-600" />;
      case 'claim':
        return <TrendingUp className="h-4 w-4 text-blue-600" />;
      case 'compound':
        return <TrendingUp className="h-4 w-4 text-purple-600" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">Completed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">Pending</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAddress = (hash: string) => {
    return `${hash.slice(0, 6)}...${hash.slice(-4)}`;
  };

  const filteredTransactions = filter === 'all' 
    ? transactions 
    : transactions.filter(tx => tx.type === filter);

  return (
    <div className="space-y-6">
      {/* Analytics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Staked</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalStaked.toLocaleString()} NDH</div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Since {analytics.firstStakeDate.toLocaleDateString()}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Rewards</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{analytics.totalRewardsClaimed.toFixed(2)} NDH</div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              ≈ ${(analytics.totalRewardsClaimed * 0.89).toFixed(2)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Avg. Stake Duration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.averageStakeDuration} days</div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Across all pools
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Gas Fees Paid</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalGasPaid.toFixed(3)} ETH</div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              ≈ ${(analytics.totalGasPaid * 2845.67).toFixed(2)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Transaction History */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Transaction History</CardTitle>
          <Button variant="outline" size="sm">
            Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="stake">Stakes</TabsTrigger>
              <TabsTrigger value="unstake">Unstakes</TabsTrigger>
              <TabsTrigger value="claim">Claims</TabsTrigger>
              <TabsTrigger value="compound">Compounds</TabsTrigger>
            </TabsList>

            <TabsContent value={filter} className="mt-6">
              <div className="space-y-4">
                {filteredTransactions.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                        {getTransactionIcon(tx.type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium capitalize">{tx.type}</span>
                          {getStatusBadge(tx.status)}
                        </div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">
                          {tx.pool} • {formatDate(tx.date)}
                        </div>
                        <div className="text-xs text-slate-400 dark:text-slate-500">
                          {formatAddress(tx.hash)} • Gas: {parseInt(tx.gasUsed).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right flex items-center gap-3">
                      <div>
                        <div className="font-medium">
                          {tx.type === 'unstake' ? '-' : '+'}{tx.amount.toFixed(2)} NDH
                        </div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">
                          ≈ ${(tx.amount * 0.89).toFixed(2)}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {filteredTransactions.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-slate-500 dark:text-slate-400">
                    No {filter === 'all' ? '' : filter} transactions found.
                  </p>
                </div>
              )}

              {filteredTransactions.length > 0 && (
                <div className="mt-6 text-center">
                  <Button variant="outline">
                    Load More Transactions
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}